import { Plus, FolderPlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export interface EmptyFilesDropZoneProps {
  isDraggingOver: boolean;
  isMaxDepth: boolean;
  isUploadingToFolder: boolean;
  isViewer?: boolean; // 🔥 是否为 viewer 角色（只读模式）
  currentPath: { name: string }[];
  onDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragLeave: (e: React.DragEvent<HTMLDivElement>) => void;
  onDrop: (e: React.DragEvent<HTMLDivElement>) => Promise<void>;
  onCreateFolder: () => void;
  onUploadClick: () => void;
}

export function EmptyFilesDropZone({
  isDraggingOver,
  isMaxDepth,
  isUploadingToFolder,
  isViewer = false,
  currentPath,
  onDragOver,
  onDragLeave,
  onDrop,
  onCreateFolder,
  onUploadClick,
}: EmptyFilesDropZoneProps) {
  const { t } = useTranslation();
  return (
    <>
      <div
        className={`p-5 sm:p-8 rounded-2xl border-2 border-dashed transition-all ${
          isDraggingOver && !isViewer
            ? 'bg-blue-50 border-blue-500'
            : 'bg-[#f5f4ef] border-gray-300 hover:border-blue-400 hover:bg-blue-50/30'
        }`}
        onDragOver={isViewer ? undefined : onDragOver}
        onDragLeave={isViewer ? undefined : onDragLeave}
        onDrop={isViewer ? undefined : onDrop}
      >
        {/* File upload drop zone */}
        <div className="flex flex-col items-center gap-3 sm:gap-4">
          {/* Icon with styled border */}
          <div
            className={`relative p-4 sm:p-6 rounded-xl border-2 border-dashed transition-colors ${
              isDraggingOver
                ? 'border-blue-500 bg-blue-100'
                : 'border-gray-300 bg-white'
            }`}
          >
            <img
              src="/static/project-knowledge-light-mode.svg"
              alt={t('project.uploadFiles')}
              className="w-16 h-16"
            />
          </div>

          {isDraggingOver ? (
            <p className="text-lg font-medium text-blue-600">
              {t('project.dropFilesToUpload')}
            </p>
          ) : (
            <>
              <div className="text-center">
                <h3 className="text-lg font-medium text-gray-700 mb-2">
                  {t('project.dropFilesToUpload')}
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  {t('project.orClickButtonBelow')}
                </p>
              </div>

              {/* 🔥 viewer 角色禁用 Upload */}
              <button
                onClick={onUploadClick}
                disabled={isUploadingToFolder || isViewer}
                className={`px-8 py-2.5 text-sm rounded-lg border transition-colors flex items-center gap-2 disabled:opacity-50 min-w-[200px] justify-center ${
                  isViewer
                    ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                }`}
                title={
                  isViewer ? t('project.viewersCannotUploadFiles') : undefined
                }
              >
                {isUploadingToFolder ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b border-white"></div>
                    {t('project.uploading')}
                  </>
                ) : (
                  <>
                    <Plus size={16} />
                    {t('project.uploadFilesButton')}
                  </>
                )}
              </button>

              <div className="text-center space-y-2">
                <p className="text-sm text-gray-500">
                  {currentPath.length > 0
                    ? t('project.addFilesInFolder')
                    : t('project.addFilesInProject')}
                </p>
                <p className="text-gray-400">
                  <span className="text-red-400">
                    {t('project.extractTips')}
                  </span>
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Create Folder button outside drop zone (hidden at max depth) */}
      {/* 🔥 viewer 角色禁用 Create Folder */}
      {!isMaxDepth && (
        <button
          onClick={onCreateFolder}
          disabled={isUploadingToFolder || isViewer}
          className={`w-full mt-3 sm:mt-4 px-4 py-2.5 border text-sm rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50 ${
            isViewer
              ? 'bg-gray-400 text-gray-200 border-gray-300 cursor-not-allowed'
              : 'bg-gray-800 text-white border-gray-300 hover:bg-gray-700'
          }`}
          title={isViewer ? t('project.viewersCannotCreateFolders') : undefined}
        >
          <FolderPlus size={16} />
          {t('project.createFolder')}
        </button>
      )}
    </>
  );
}

export default EmptyFilesDropZone;
